import type { ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	CHAT_STORE_STATUS,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
/**
 * Merges conversation data from local state and API response with network priority.
 * Prioritizes fields from the API (channelsFromAPI) as they represent the latest server state.
 * Falls back to local state (localConversation) for fields missing in the API response.
 */
export const mergeConversationWithNetworkPriority = (
	remoteConversation: ConversationChannel,
	localConversation?: Conversation,
): Conversation => {
	return {
		id: remoteConversation.id,

		// Prioritize API data if present, otherwise use local or generate new
		name: remoteConversation.name ?? localConversation?.name ?? generateConversationName(),
		timeCreated: remoteConversation.time_created ?? localConversation?.timeCreated,
		lastMessage: remoteConversation.last_message ?? localConversation?.lastMessage ?? undefined,
		lastMessageType:
			remoteConversation.last_message_type ?? localConversation?.lastMessageType ?? [],
		lastMessageTimestamp:
			remoteConversation.last_message_timestamp ?? localConversation?.lastMessageTimestamp,
		lastAgentMessageStatus:
			remoteConversation.last_agent_message_status ?? localConversation?.lastAgentMessageStatus,
		read_status: remoteConversation.read_status ?? localConversation?.read_status,

		// Use local state data if it exists (messages, status are maintained locally)
		messages: localConversation?.messages ?? [],
		status: localConversation?.status ?? CHAT_STORE_STATUS.idle,
		traces: localConversation?.traces ?? [],

		...(localConversation?.sourceAgentId ? { sourceAgentId: localConversation.sourceAgentId } : {}),
		...(localConversation?.sourceId ? { sourceId: localConversation.sourceId } : {}),
		// Preserve runtime abort controller so Stop button works after channels rehydration (e.g. refocus)
		...(localConversation?.storeAbortController != null
			? { storeAbortController: localConversation.storeAbortController }
			: {}),
		agent: remoteConversation.agent,
	};
};
